
import AddUserIcon from '@/img/AddContact.png';
import RemoveUserIcon from '@/img/RemoveContact.png';
import AddImgIcon from '@/img/AddImg.png';
import { ImagePlus, UserRoundPlus, UserRoundMinus } from "lucide-react";

import { ContactSchema, CustomerInfoSchema, CustomerSchema } from './CustomerSchema';
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel
} from "@/components/ui/form"
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useFieldArray } from "react-hook-form";
import { useApiFetch } from "@/helper/apiFetch";
import { useTranslate } from '@/helper/translate';
import { Checkbox } from '../ui/checkbox';
type CustomerInfoProps = {
  customerId?: string;
  onClose: (customer: CustomerInfoSchema | null) => void;
};


export function CustomerInfo({ customerId, onClose }: CustomerInfoProps) {
  const apiFetch = useApiFetch();
  const { translate } = useTranslate();
  const [contactIndex, setIndex] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorText, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm<z.infer<typeof CustomerSchema>>({
    resolver: zodResolver(CustomerSchema),
    defaultValues: {
      customerId: "00000000-0000-0000-0000-000000000000",
      isActive: true,
      contact: {
        contactId: "00000000-0000-0000-0000-000000000000"
      },
      contacts: []
    }
  });

  const contacts = form.watch("contacts") || [];

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact
  } = useFieldArray({
    control: form.control,
    name: "contacts"
  });

  const addContact = () => {
    const blankContact = ContactSchema.parse({});
    appendContact(blankContact);
    setIndex(contacts.length); // jump to the new contact
  };
  const logo = form.watch("contact.picture.data"); // subscribe!

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";  
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = (reader.result as string).split(",")[1]; // strip prefix
      form.setValue("contact.picture.data", base64);

    };
    reader.readAsDataURL(file);
  };


  const onSubmit = async (values: z.infer<typeof CustomerSchema>) => {
    try {
      setError("");
      setIsSaving(true);
      const res = await apiFetch(`/App/SaveCustomer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!res.ok) {
        const e = await res.text();
        setError(translate("ui.failedsave") + e);
        setIsSaving(false);
      }
      else {
        //const formValues = form.getValues();
        const data = await res.json();

        // const returnValue: CustomerInfoSchema = {
        //   id: formValues.customerId??"00000000-0000-0000-0000-000000000000",
        //   name: formValues.contact.last??"",
        //   address: (formValues.contact.address.street??"") + "\n" + (formValues.contact.address.postalCode??"" ) + " " + (formValues.contact.address.city??""),
        //   phone: formValues.contact.phone??"",
        //   email: formValues.contact.email??"",
        //   logo: formValues.contact.picture.data?? "",
        //   isDeleted: false
        // };
        onClose(data as CustomerInfoSchema);
        //const data = await res.json();
        //form.reset(data);//to set the ids
      }
    } catch (err) {
      console.error(err);
    }
  };





  useEffect(() => {
    if (!customerId) { setIsLoaded(true); return; }

    apiFetch(`/App/GetCustomer?id=${customerId}`)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then(data => {
        form.reset(data);
        setIsLoaded(true);
      })
      .catch(err => {
        console.error(err);
        //setLoading(false);
      });
  }, []);


  return (

    <div className="popCnt p-0">

      {!isLoaded ? (
        <div className="w-full text-center my-10">{translate("common.loading")}</div>
      ) : (
        <Form {...form}>

          <form onSubmit={form.handleSubmit(onSubmit, (error) => { console.log("Validation errors:", error); })} className="space-y-3">

          <div className="bg-white dark:bg-zinc-600 rounded-2xl shadow-lg space-y-4 mx-[-10px] mt-1 p-3">

            <div className=" grid-default">
              <FormField
                control={form.control}
                name="contact.last"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate("ui.company")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-3 gap-4">
                {logo ? (
                  <img
                    src={`data:image/png;base64,${logo}`}
                    className="col-span-2 cursor-pointer"
                    onClick={handleImageClick}
                  />
                ) : (
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full col-span-3 h-[150px] flex flex-col" 
                  onClick={handleImageClick}
                >
                  <ImagePlus className="!w-[40px] !h-[40px]" strokeWidth={1.25} alt="Add Logo" />
                  {translate("ui.addLogo")}
                </Button>
                  /*
                  <img
                    src={AddImgIcon}
                    alt="Add Logo"
                    className="col-span-2 cursor-pointer"
                    onClick={handleImageClick}
                  />
                  */
                )}

                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />


              </div>
              <FormField
                control={form.control}
                name="contact.address.street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate("ui.street")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={form.control}
                  name="contact.address.postalCode"
                  render={({ field }) => (
                    <FormItem className="col-span-1">
                      <FormLabel>{translate("ui.zip")}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact.address.city"
                  render={({ field }) => (
                    <FormItem className="col-span-2">
                      <FormLabel>{translate("ui.city")}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                /></div>
              <FormField
                control={form.control}
                name="contact.website"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate("ui.website")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="grid-default">
                <FormField
                  control={form.control}
                  name="contact.phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("ui.phone")}</FormLabel>
                      <FormControl>
                        <Input type="tel" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact.fax"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("ui.fax")}</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="contact.email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{translate("ui.email")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
             
                <FormField
                  control={form.control}
                  name="fileDirectory"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{translate("ui.foldername")}</FormLabel>
                      <FormControl >
                        <Input disabled={customerId != ""} {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              

                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem>
                     <FormControl>
                        <Checkbox
                          label={translate("ui.status_active")}
                          checked={!!field.value}
                          onCheckedChange={field.onChange}
                          name={field.name}
                          onBlur={field.onBlur}
                          ref={field.ref} 
                          className="mt-6"
                          />
                          
                      </FormControl>
                    </FormItem>
                  )}
                />
              

            </div>
            {contacts.length > 0 && contacts[contactIndex] && (
              <>

                <div className="tabs">
                  {contacts.map((contact, index) => (
                    <span
                      key={index}
                      className={`tab ${index === contactIndex ? 'active' : ''}`}
                      onClick={() => setIndex(index)}
                    >
                      {contact.last}, {contact.first}
                    </span>
                  ))}
                </div>

                <div >
                  {contacts[contactIndex] && (
                    <div className="grid-default" key={contactIndex}>
                      <FormField
                        control={form.control}
                        name={`contacts.${contactIndex}.last`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{translate("ui.lastname")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`contacts.${contactIndex}.first`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{translate("ui.firstname")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`contacts.${contactIndex}.email`}
                        render={({ field }) => (
                          <FormItem className="col-span-2">
                            <FormLabel>{translate("ui.email")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`contacts.${contactIndex}.phone`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{translate("ui.phone")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`contacts.${contactIndex}.fax`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{translate("ui.fax")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name={`contacts.${contactIndex}.mobile`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{translate("ui.cell")}</FormLabel>
                            <FormControl>
                              <Input {...field} />
                            </FormControl>
                          </FormItem>
                        )}
                      />

                    </div>
                  )}

                </div>


              </>
            )}
          </div>

            <div className="grid-default">
              <div className="flex items-center">
                <div onClick={addContact} className="cursor-pointer">
                  {/* <img src={AddUserIcon} alt="Add User" style={{ width: "32px" }} /> */}
                  <UserRoundPlus className="w-8 h-8 cursor-pointer bg-teal-600 hover:bg-teal-700 rounded-full text-white p-[4px]" alt="Add User" />
                </div>
                {contacts.length > 0 && contacts[contactIndex] && (
                  <div onClick={() => removeContact(contactIndex)} className="cursor-pointer">
                  {/* <img src={RemoveUserIcon} alt="Add User" style={{ width: "32px" }} /> */}
                    <UserRoundMinus className="w-8 h-8 cursor-pointer bg-rose-600 hover:bg-rose-700 rounded-full text-white p-[4px] ml-2" alt="Delete User" />
                  </div>
                )}
              </div>
              <div className="grid-default">
                <Button className="w-full" type="button" onClick={() => { onClose(null); }}>{translate("common.cancel")}</Button>

                <Button className="w-full" type="submit" disabled={isSaving}>{translate("common.save")}</Button>
              </div>
            </div>
            {(errorText != "") && (
              <div className="text-red-500 mt-2">
                {errorText}
              </div>
            )
            }

          </form>
        </Form>
      )}
    </div>

  )
}



